import { DEFAULT_HELP_LANGUAGE } from "@/utils/help-language.ts";

export interface HelpArticleMetadata {
  id: string;
  title: string;
  description: string;
  icon?: string;
  module?: string;
  roles?: string[];
}
export interface HelpArticle extends HelpArticleMetadata {
  file: string;
  lastModified: string;
  category: string;
  sourceLanguage: string;
}

export interface HelpCollectionMetadata {
  id: string;
  title: string;
  description: string;
  emoji?: string;
}

export interface HelpCollection extends HelpCollectionMetadata {
  children: HelpCollection[];
  articleCount: number;
}

export interface HelpIndex {
  categories: HelpCollection[];
  articles: HelpArticle[];
  searchIndex: SearchIndexItem[];
}

export interface SearchIndexItem {
  id: string;
  type: "category" | "article";
  title: string;
  description: string;
  content: string; // полный текст для поиска
  category?: string;
}

export interface BreadcrumbItem {
  id: string;
  title: string;
  url: string;
}

interface CachedHelpIndex {
  data: HelpIndex;
  timestamp: number;
  lang: string;
}

class HelpIndexCache {
  private cache = new Map<string, CachedHelpIndex>();
  private readonly CACHE_DURATION = 10 * 60 * 1000;

  private async fetchIndex(lang: string): Promise<HelpIndex> {
    const timestamp = Date.now();
    const response = await fetch(`/help/${lang}/help-index.json?t=${timestamp}`);

    if (!response.ok) {
      throw new Error(`Failed to load help index: ${response.status}`);
    }

    return response.json();
  }

  async getIndex(lang: string): Promise<HelpIndex> {
    const cacheKey = lang;
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data;
    }

    try {
      const data = await this.fetchIndex(lang);

      this.cache.set(cacheKey, {
        data,
        timestamp: Date.now(),
        lang,
      });

      return data;
    } catch (error) {
      if (lang !== DEFAULT_HELP_LANGUAGE) {
        try {
          const fallbackData = await this.fetchIndex(DEFAULT_HELP_LANGUAGE);

          this.cache.set(cacheKey, {
            data: fallbackData,
            timestamp: Date.now(),
            lang,
          });

          return fallbackData;
        } catch (fallbackError) {
          console.error(`Failed to load fallback help index for ${lang}:`, fallbackError);
        }
      }

      if (cached) {
        return cached.data;
      }

      throw error;
    }
  }
}

//   // Очистить кэш (для dev режима)
//   clearCache(lang?: string) {
//     if (lang) {
//       this.cache.delete(lang);
//       console.log(`🗑️ Cleared cache for ${lang}`);
//     } else {
//       this.cache.clear();
//       console.log(`🗑️ Cleared all help cache`);
//     }
//   }
//
//   // Предзагрузка для всех языков
//   async preloadAll(languages: string[] = ["ru", "en"]) {
//     console.log("🚀 Preloading help indexes...");
//     await Promise.all(
//       languages.map((lang) =>
//         this.getIndex(lang).catch((e) => console.warn(`Failed to preload ${lang}:`, e))
//       )
//     );
//   }
//
//   // Получить статус кэша
//   getCacheStatus() {
//     const status = Array.from(this.cache.entries()).map(([lang, cached]) => ({
//       lang,
//       age: Date.now() - cached.timestamp,
//       fresh: Date.now() - cached.timestamp < this.CACHE_DURATION,
//     }));
//
//     console.table(status);
//     return status;
//   }
// }

export const helpIndexCache = new HelpIndexCache();
